import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Building2, Users, GraduationCap, TrendingUp, 
  ChevronRight, BarChart3, School, MapPin
} from 'lucide-react';
import axios from 'axios';
import MosaicDistrictRollup from '@/components/mosaic/MosaicDistrictRollup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SchoolAdminDashboard = () => {
  const { user } = useAuth();
  const [school, setSchool] = useState(null);
  const [stats, setStats] = useState(null);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (user?.school_id) {
          const [schoolRes, statsRes, usersRes] = await Promise.all([
            axios.get(`${API}/schools/${user.school_id}`),
            axios.get(`${API}/schools/${user.school_id}/stats`),
            axios.get(`${API}/users?role=teacher`)
          ]);
          
          setSchool(schoolRes.data);
          setStats(statsRes.data);
          setTeachers(usersRes.data.users || []);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [user]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="school-admin-dashboard">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
          <Building2 size={16} />
          <span>School Administration</span>
        </div>
        <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {school?.name || 'School Dashboard'}
        </h1>
        {school?.borough && (
          <p className="text-gray-600 flex items-center gap-2">
            <MapPin size={16} />
            {school.borough}, NYC • DBN: {school.dbn || 'N/A'}
          </p>
        )}
      </div>

      {/* v6.32.166 — MOSAIC District Rollup (school-scoped view shows just this school's classes) */}
      <div className="mb-8">
        <MosaicDistrictRollup />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border-t border-l border-gray-300 mb-8">
        <div className="swiss-card cursor-pointer hover:bg-blue-50 transition-colors" onClick={() => setActiveTab('overview')} data-testid="stat-students">
          <Users className="w-8 h-8 text-blue-600 mb-3" />
          <div className="text-3xl font-bold">{stats?.students || 0}</div>
          <div className="text-sm text-gray-500">Students</div>
        </div>
        <div className="swiss-card cursor-pointer hover:bg-green-50 transition-colors" onClick={() => setActiveTab('teachers')} data-testid="stat-teachers">
          <GraduationCap className="w-8 h-8 text-green-600 mb-3" />
          <div className="text-3xl font-bold">{stats?.teachers || 0}</div>
          <div className="text-sm text-gray-500">Teachers</div>
        </div>
        <div className="swiss-card cursor-pointer hover:bg-purple-50 transition-colors" onClick={() => setActiveTab('grades')} data-testid="stat-exams">
          <BarChart3 className="w-8 h-8 text-purple-600 mb-3" />
          <div className="text-3xl font-bold">{stats?.total_exams || 0}</div>
          <div className="text-sm text-gray-500">Exams Completed</div>
        </div>
        <div className="swiss-card cursor-pointer hover:bg-orange-50 transition-colors" onClick={() => setActiveTab('grades')} data-testid="stat-avg-score">
          <TrendingUp className="w-8 h-8 text-orange-600 mb-3" />
          <div className="text-3xl font-bold">{stats?.average_score || 0}</div>
          <div className="text-sm text-gray-500">Avg Proficiency Score</div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="border-b border-gray-200 bg-transparent p-0 mb-6">
          <TabsTrigger value="overview" className="modality-tab data-[state=active]:active">
            Overview
          </TabsTrigger>
          <TabsTrigger value="teachers" className="modality-tab data-[state=active]:active">
            Teachers ({teachers.length})
          </TabsTrigger>
          <TabsTrigger value="grades" className="modality-tab data-[state=active]:active">
            By Grade Level
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Performance by Grade */}
            <div className="border-2 border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-bold">Performance by Grade</h3>
              </div>
              <div className="p-4 space-y-4">
                {stats?.by_grade && Object.entries(stats.by_grade).length > 0 ? (
                  Object.entries(stats.by_grade).map(([grade, data]) => (
                    <div key={grade}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Grade {grade}</span>
                        <span className="text-gray-500">{data.avg_score} avg ({data.count} exams)</span>
                      </div>
                      <Progress value={data.avg_score} className="h-2" />
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 text-center py-8">No exam data available</p>
                )}
              </div>
            </div>

            {/* Quick Actions */}
            <div className="border-2 border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h3 className="font-bold">Administration</h3>
              </div>
              <div className="p-4 space-y-3">
                <Link to="/results" className="block">
                  <Button variant="outline" className="w-full btn-sharp justify-start h-12">
                    <BarChart3 className="mr-3" size={18} />
                    View All Results
                  </Button>
                </Link>
                <Link to="/teacher-key" className="block">
                  <Button variant="outline" className="w-full btn-sharp justify-start h-12">
                    <GraduationCap className="mr-3" size={18} />
                    Assessment Materials
                  </Button>
                </Link>
                <Link to="/grades" className="block">
                  <Button className="w-full btn-sharp-primary justify-start h-12">
                    <School className="mr-3" size={18} />
                    Start New Assessment
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Teachers Tab */}
        <TabsContent value="teachers">
          <div className="border-2 border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold">Teaching Staff</h3>
            </div>
            {teachers.length === 0 ? (
              <div className="p-12 text-center text-gray-500">
                <GraduationCap className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No teachers assigned to this school yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {teachers.map(teacher => (
                  <div key={teacher.user_id} className="flex items-center justify-between p-4">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-700 font-semibold">
                        {teacher.name?.charAt(0) || 'T'}
                      </div>
                      <div>
                        <div className="font-semibold">{teacher.name}</div>
                        <div className="text-sm text-gray-500">{teacher.email}</div>
                      </div>
                    </div>
                    <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium">
                      Teacher
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        {/* By Grade Tab */}
        <TabsContent value="grades">
          <div className="border-2 border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold">Grade Level Analytics</h3>
            </div>
            {stats?.by_grade && Object.entries(stats.by_grade).length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0 border-t border-l border-gray-200">
                {Object.entries(stats.by_grade).map(([grade, data]) => (
                  <div key={grade} className="swiss-card">
                    <div className="text-lg font-bold mb-2">Grade {grade}</div>
                    <div className="text-4xl font-bold text-[#002FA7] mb-2">
                      {data.avg_score}
                    </div>
                    <div className="text-sm text-gray-500">
                      {data.count} exams completed
                    </div>
                    <Progress value={(data.avg_score / 6) * 100} className="h-2 mt-3" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-12 text-center text-gray-500">
                <BarChart3 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p>No grade-level data available yet</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SchoolAdminDashboard;
